// i18n
import 'src/locales/i18n';

// scrollbar
import 'simplebar-react/dist/simplebar.min.css';

// lightbox
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';

// map
import 'mapbox-gl/dist/mapbox-gl.css';

// editor
import 'react-quill/dist/quill.snow.css';

// carousel
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// image
import 'react-lazy-load-image-component/src/effects/blur.css';

// ----------------------------------------------------------------------

// @mui
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// routes
import Router from 'src/routes/sections';
// theme
import ThemeProvider from 'src/theme';
// hooks
import { useScrollToTop } from 'src/hooks/use-scroll-to-top';
// components
import ProgressBar from 'src/components/progress-bar';
import MotionLazy from 'src/components/animate/motion-lazy';
import SnackbarProvider from 'src/components/snackbar/snackbar-provider';
import { SettingsProvider, SettingsDrawer } from 'src/components/settings';
// sections
import { CheckoutProvider } from 'src/sections/checkout/context';

import PendingCountsInitializer from 'src/components/PendingCountsInitializer';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { localLogout } from 'src/redux/slices/authSlice';
import { clearSetupChecklist } from 'src/redux/slices/setupChecklistSlice';
import { DateFormatProvider } from './helper/date-format-provider';
import store from './redux/store';
import AuthInitializer from './AuthInitializer';
import { injectStoreNavigateAndLogout, listenForAuthSync, setMachineIP } from './api/axiosInstance';
import useFingerprint from './hooks/useFingerprint';
import { initIPCapture } from './utils/ipCapture';
import { checkAuth, initializeUserSession } from './redux/thunks/authThunks';
// import AuthLoader from './AuthLoader';





// ----------------------------------------------------------------------
// axios.defaults.withCredentials = true; // Enable sending cookies with requests
const getUserIdentity = (user) =>
  user?.userId || user?.id || user?.email || user?.userEmail || user?.loginId || null;

export default function App() {

  const charAt = `

  ░░░    ░░░
  ▒▒▒▒  ▒▒▒▒
  ▒▒ ▒▒▒▒ ▒▒
  ▓▓  ▓▓  ▓▓
  ██      ██

  `;

  console.info(`%c${charAt}`, 'color: #5BE49B');

  useScrollToTop();
  // const fingerprint = useFingerprint();


  // console.log('Visitor Fingerprint ========= :', fingerprint);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  // ✅ Unified logout function using Redux
  const logout = useCallback(() => {
    dispatch(localLogout());
    dispatch(clearSetupChecklist());
  }, [dispatch]);

  useEffect(() => {
    injectStoreNavigateAndLogout({
      storeM: store,
      navigate,
      // logout: () => dispatch(localLogout()),
      logout
    });
  }, [navigate, dispatch, logout]);


  useEffect(() => {
    const handleLoginSync = async () => {
      const previousUserId = getUserIdentity(store.getState().auth.user);

      try {
        await dispatch(checkAuth()).unwrap();
        const session = await dispatch(initializeUserSession()).unwrap();
        const nextUserId = getUserIdentity(session?.user);

        if (previousUserId && nextUserId && previousUserId !== nextUserId) {
          navigate('/dashboard', { replace: true });
          window.location.reload();
        }
      } catch {
        // Session invalid or race condition – ignore
      }
    };

    const handleLogoutSync = () => {
      dispatch(localLogout());
      navigate('/auth/jwt/login', { replace: true });
      window.location.reload();
    };

    listenForAuthSync(handleLoginSync, handleLogoutSync);
  }, [dispatch, navigate]);

  // ✅ Apply auto-logout using same logout logic
  // useEffect(() => {
  //   initAutoLogout(logout);
  // }, [logout]);

  // IP Capture and Injection into axios
  initIPCapture(setMachineIP);

  const dateFormat = useSelector((state) => state.tenantPreference.dateFormat);
  const mappedFormat = dateFormat ? dateFormat.replace(/D/g, 'd').replace(/Y/g, 'y') : 'dd/MM/yyyy';

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      dateFormats={{
        keyboardDate: mappedFormat,
      }}
    >
      <SettingsProvider
        defaultSettings={{
          themeMode: 'light', // 'light' | 'dark'
          themeDirection: 'ltr', //  'rtl' | 'ltr'
          themeContrast: 'default', // 'default' | 'bold'
          themeLayout: 'vertical', // 'vertical' | 'horizontal' | 'mini'
          themeColorPresets: 'default', // 'default' | 'cyan' | 'purple' | 'blue' | 'orange' | 'red'
          themeStretch: false,
          fullscreen: false,
        }}
      >
        <ThemeProvider dateFormat={dateFormat}>
          <MotionLazy>
            <SnackbarProvider>
              <CheckoutProvider>
                <DateFormatProvider>
                  <PendingCountsInitializer />
                  <SettingsDrawer />
                  <ProgressBar />
                  <AuthInitializer />
                  {/* <AuthLoader> */}
                  <Router />
                  {/* </AuthLoader> */}
                </DateFormatProvider>
              </CheckoutProvider>
            </SnackbarProvider>
          </MotionLazy>
        </ThemeProvider>
      </SettingsProvider>
    </LocalizationProvider>
  );
}


